
import { useState, useEffect } from "react";
import toast from "react-hot-toast";

export const useInventoryForm = (activeTab, masterItems, inventory, dynamicSettings = {}) => {
  const [form, setForm] = useState({
    name: "", sku: "", itemCode: "", category: "", unit: "", unitPrice: 0, minQty: 0, availableQty: 0, supplier: "", serviceType: activeTab,
    itemType: "", brand: "", hsnCode: "", isUnique: false, uniqueName: "", hasBatchExpiry: false, expiryDate: "", barcode: "", description: "",
    invoiceNo: "", invoiceDate: new Date().toISOString().split('T')[0], businessUnit: "VENUS INFRA SERVICES", store: "",
    rate: 0, quantity: 1, baseAmount: 0, discP: 0, discount: 0, taxable: 0, taxP: 0, bvPoints: 0,
    igst: 0, cgst: 0, sgst: 0, taxAmount: 0,
    netBill: 0, chargeAmount: 0, adjustments: 0, netPayment: 0,
    items: []
  });
  // Note: Auto-defaulting of first items removed to allow "Select..." placeholders to show by default.
  useEffect(() => {
    // Keep form clean unless explicitly set
  }, [dynamicSettings]);

  // Real-time calculations for the form
  useEffect(() => {
    const rate = Number(form.rate) || 0;
    const qty = Number(form.quantity) || 1;
    const discP = Number(form.discP) || 0;
    const igst = Number(form.igst) || 0;
    const sgst = Number(form.sgst) || 0;
    const cgst = Number(form.cgst) || 0;
    const taxP = Number(form.taxP) || 0;

    const baseAmount = rate * qty;
    const discountAmount = (baseAmount * discP) / 100;
    const taxable = baseAmount - discountAmount;

    let taxTotal = igst + cgst + sgst;
    if (taxP > 0) {
      taxTotal = (taxable * taxP) / 100;
    }

    const netBill = taxable + taxTotal;
    const itemsTotal = (form.items || []).reduce((sum, it) => sum + (Number(it.netPayment) || 0), 0);
    const netPayment = netBill + (Number(form.chargeAmount) || 0) + (Number(form.adjustments) || 0) + itemsTotal;

    setForm(prev => {
      if (!prev.name && (prev.items?.length || 0) > 0) {
          const currentTotal = itemsTotal + Number(prev.adjustments || 0);
          if (prev.netPayment === currentTotal) return prev;
          return { ...prev, netPayment: currentTotal };
      }

      if (
        prev.baseAmount === baseAmount &&
        prev.discount === discountAmount &&
        prev.taxable === taxable &&
        prev.taxAmount === taxTotal &&
        prev.netBill === netBill &&
        prev.netPayment === netPayment &&
        Math.abs(Number(prev.unitPrice || 0) - (qty > 0 ? (netBill / qty) : Number(prev.unitPrice || 0))) < 0.01
      ) return prev;

      return {
        ...prev,
        baseAmount,
        discount: discountAmount,
        taxable,
        taxAmount: taxTotal,
        netBill,
        netPayment,
        unitPrice: qty > 0 ? (netBill / qty) : prev.unitPrice, 
        availableQty: qty 
      };
    });
  }, [form.rate, form.quantity, form.discP, form.igst, form.cgst, form.sgst, form.chargeAmount, form.adjustments, form.taxP, form.items?.length]);

  const handleSelectExistingItem = (itemOrId) => {
    console.log("handleSelectExistingItem called with:", itemOrId);
    
    const item = typeof itemOrId === 'string' 
      ? (masterItems.find(mi => mi._id === itemOrId) || inventory.find(i => i._id === itemOrId))
      : itemOrId;
      
    if (!item) {
      console.warn("Item not found for selection:", itemOrId);
      return;
    }

    console.log("Populating form with item:", item);
    console.log("isUnique:", item.isUnique, "| hasBatch:", item.hasBatch, "| isBatchExpiry:", item.isBatchExpiry, "| hasBatchExpiry:", item.hasBatchExpiry);

    const getId = (field) => {
      if (!field) return "";
      if (typeof field === "object") return field._id || field.id || field.title || field.name || field.categoryName || field.brandName || field.unitName || "";
      return field;
    };

    const itemCategory = getId(item.category);
    const itemUnit = getId(item.units || item.unit);
    const itemBrand = getId(item.brand);

    const rawType = item.itemType || item.type || "Product";
    const normalizedType = rawType === 'Stockable' ? 'Product' : (rawType === 'Consumable' ? 'Material' : rawType);
    
    // Ensure we always use the Master Item ID, even if an Inventory record was selected
    const masterId = item.item?._id || (typeof item.item === 'string' ? item.item : item._id);

    // The DB model stores: isUnique (Boolean), hasBatch (Boolean)
    const isUniqueVal = item.isUnique === true || item.isSerialManaged === true;
    const hasBatchVal = item.hasBatch === true || item.isBatchExpiry === true || item.hasBatchExpiry === true;

    setForm(prev => ({
      ...prev,
      _id: masterId, 
      inventoryId: item.item ? item._id : undefined,
      name: item.itemName || item.name || "",
      itemCode: item.itemCode || item.code || "",
      unit: itemUnit,
      category: itemCategory,
      brand: itemBrand,
      itemType: normalizedType,
      hsnCode: item.hsnSacCode || item.hsnCode || "",
      description: item.itemDescription || item.description || "",
      isUnique: isUniqueVal,
      uniqueName: item.uniqueName || "",
      barcode: item.barcode || "",
      hasBatchExpiry: hasBatchVal,
      taxP: item.taxPercent || item.taxP || 0,
      bvPoints: item.bvPoints || 0,
      minQty: item.minQty || 0,
      availableQty: item.stockQty || item.availableQty || 0,
      unitPrice: 0,
      rate: 0,
      quantity: 1
    }));
  };

  const addItemToList = () => {
    if (!form.name) {
      toast.error("Please enter/select an item name first");
      return;
    }

    if (!form.rate || Number(form.rate) <= 0) {
      toast.error("Purchase Amount/Rate is mandatory and must be greater than 0");
      return;
    }

    if (!form.quantity || Number(form.quantity) <= 0) {
      toast.error("Quantity is mandatory and must be greater than 0");
      return;
    }

    const newItem = {
      ...form,
      id: Date.now(),
      items: [],
      netPayment: form.netBill // Ensure individual item total only reflects its own net bill
    };

    setForm(prev => ({
      ...prev,
      items: [...(prev.items || []), newItem],
      // Reset only item-specific fields
      name: "", sku: "", itemCode: "", category: "", unit: "", unitPrice: 0, minQty: 0, availableQty: 0,
      brand: "", itemType: "", hsnCode: "", isUnique: false, uniqueName: "", hasBatchExpiry: false, 
      expiryDate: "", barcode: "", description: "",
      rate: 0, quantity: 0, baseAmount: 0, discP: 0, discount: 0, taxable: 0, taxP: 0, bvPoints: 0,
      igst: 0, cgst: 0, sgst: 0, taxAmount: 0, netBill: 0, chargeAmount: 0, adjustments: 0
    }));
    toast.success("Item added to list");
  };

  const removeItemFromList = (id) => {
    setForm(prev => ({
      ...prev,
      items: prev.items.filter(it => it.id !== id)
    }));
  };

  const editItemInList = (id) => {
    const item = form.items.find(it => it.id === id);
    if (!item) return;
    
    setForm(prev => ({
      ...prev,
      ...item,
      items: prev.items.filter(it => it.id !== id)
    }));
  };

  const resetForm = (initialData = {}) => {
    setForm({
      name: "", sku: "", itemCode: "", category: "", unit: "", unitPrice: 0, minQty: 0, availableQty: 0, supplier: "", serviceType: activeTab,
      itemType: "", brand: "", hsnCode: "", isUnique: false, uniqueName: "", hasBatchExpiry: false, expiryDate: "", barcode: "", description: "",
      invoiceNo: "", invoiceDate: new Date().toISOString().split('T')[0], businessUnit: "VENUS INFRA SERVICES", store: "",
      rate: 0, quantity: 0, baseAmount: 0, discP: 0, discount: 0, taxable: 0, taxP: 0, bvPoints: 0,
      igst: 0, cgst: 0, sgst: 0, taxAmount: 0,
      netBill: 0, chargeAmount: 0, adjustments: 0, netPayment: 0,
      items: [],
      ...initialData
    });
  };

  return {
    form, setForm,
    handleSelectExistingItem,
    addItemToList,
    removeItemFromList,
    editItemInList,
    resetForm
  };
};
